import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useTranslate } from 'src/localization/Trns'
import BudgetsAppRoute from 'src/navigation/appRoutes/budgets.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  budgetsAllBudgetsUrl,
  budgetsNeedsApprovalUrl,
  createBudgetUrl,
  requestBudgetUrl,
} from 'src/navigation/urls'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectBudgetsAllBudgetsMatch,
  selectBudgetsNeedsApprovalMatch,
  selectBudgetsRequestsMatch,
  selectCalculatedBackgroundLocation,
  selectMyWalletBudgetsMatch,
  selectMyWalletBudgetsPendingMatch,
} from 'src/redux/selectors/router.selectors'

const Budgets: React.FC = () => {
  const translate = useTranslate()

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const budgetsAllBudgetsMatch = useSelector(selectBudgetsAllBudgetsMatch)
  const budgetsYourBudgetsMatch = useSelector(selectMyWalletBudgetsMatch)
  const budgetsPendingBudgetsMatch = useSelector(
    selectMyWalletBudgetsPendingMatch,
  )
  const budgetsRequestsMatch = useSelector(selectBudgetsRequestsMatch)
  const budgetsNeedsApprovalMatch = useSelector(selectBudgetsNeedsApprovalMatch)
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCountStatistics,
      appStatisticsKeys.CreditCardsDelegateCountStatistics,
      appStatisticsKeys.CreditCardsSourceCountStatistics,
    ])
  }, [dispatchStatistics])

  const canIssueBudget = creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0
  const canCurrentlyIssueBudget =
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount > 0

  const subnavOptions = useMemo(
    () => [
      {
        label: translate('Overview'),
        to: budgetsAllBudgetsUrl(),
        isActive: budgetsAllBudgetsMatch !== null,
        ariaLabelSource: 'Overview',
        'data-testid': 'AllBudgetsBudgets',
      },
      {
        label: translate('Needs Approval'),
        to: budgetsNeedsApprovalUrl(),
        isActive:
          budgetsNeedsApprovalMatch !== null || budgetsRequestsMatch !== null,
        ariaLabelSource: 'Needs Approval',
        'data-testid': 'NeedsApprovalBudgets',
      },
    ],
    [
      budgetsAllBudgetsMatch,
      budgetsYourBudgetsMatch,
      budgetsPendingBudgetsMatch,
      budgetsRequestsMatch,
      budgetsNeedsApprovalMatch,
    ],
  )

  const buttons = useMemo(
    () =>
      canIssueBudget
        ? [
            {
              design: ButtonDesigns.designCtaInverse,
              type: ButtonTypes.button,
              text: translate('Request Budget'),
              linkProps: {
                to: requestBudgetUrl(),
                ariaLabel: 'Request Budget',
              },
            },
            {
              design: ButtonDesigns.designCta,
              type: ButtonTypes.button,
              text: translate('Create Budget'),
              linkProps: {
                to: canCurrentlyIssueBudget
                  ? createBudgetUrl()
                  : actionUnavailableUrl(),
                ariaLabel: 'Create Budget',
              },
            },
          ]
        : [
            {
              design: ButtonDesigns.designCta,
              type: ButtonTypes.button,
              text: translate('Request Budget'),
              linkProps: {
                to: requestBudgetUrl(),
                ariaLabel: 'Request Budget',
              },
            },
          ],
    [canIssueBudget, canCurrentlyIssueBudget],
  )

  return !(
    statisticsLoaded.creditCardsSourceBudgetsEnabledCountStatisticsLoaded &&
    statisticsLoaded.creditCardsDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsSourceCountStatisticsLoaded
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <LayoutShell
      subnavOptions={subnavOptions}
      subnavDesign={SubnavDesign.Floating}
      content={
        <Switch location={calculatedBackgroundLocation}>
          {BudgetsAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...BudgetsAppRoute.routeProps} exact>
            <Rdrct to={budgetsAllBudgetsUrl()} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={translate('Budgets')}
      buttons={buttons}
    />
  )
}

export default Budgets
